import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { generatePath, useHistory, useParams, withRouter } from 'react-router'
import { t } from 'src/translations'

import { ApolloQueryResult } from '@apollo/client'
import { LDFlagSet } from '@launchdarkly/node-server-sdk'
import isEmpty from 'lodash/isEmpty'

import {
  EcommSettings,
  OoAvailabilityDocument,
  OoAvailabilityQuery,
  PromoBannersQuery,
  RestaurantQuery,
  useEcommSettingsQuery,
  useOoAvailabilityQuery,
  usePromoBannersQuery,
  useRestaurantQuery
} from 'src/apollo/onlineOrdering'
import {
  MenuConfig,
  OnlineOrderingConfig,
  PageType,
  RestaurantDataByDomainQueryHookResult,
  RestaurantDataByShortUrlQueryHookResult,
  RestaurantPage,
  useRestaurantDataByDomainQuery,
  useRestaurantDataByMgmtGuidQuery,
  useRestaurantDataByShortUrlQuery
} from 'src/apollo/sites'
import { reportError } from 'src/lib/js/clientError'
import { RequestContextProps } from 'src/lib/js/context'
import { siteHasIntlLocation } from 'src/lib/js/hooks/useIsIntlRestaurant'
import { getToastLocalProduct } from 'src/lib/js/trackerUtilities'
import { getHost } from 'src/lib/js/utilities'
import ToastProduct from 'src/public/components/online_ordering/ToastProduct'
import {
  isToastLocalRequest,
  isToastAppOrderRequest,
  isToastLocalSurfaceRequest
} from 'src/public/js/siteUtilities'

import {
  MetaOverrideType,
  OORestaurantOverrideType,
  useDebug
} from 'shared/components/common/debug/DebugContext'
import LoadingSpinnerOverlay from 'shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import NotFound404 from 'shared/components/errors/NotFound404'
import useDefaultSiteData, {
  RxDataUsage
} from 'shared/js/hooks/useDefaultSiteData'
import useRestaurantSiteData from 'shared/js/hooks/useRestaurantSiteData'
import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { SiteData, SiteRestaurantLocation } from 'shared/js/types'
import SitesFallback500 from 'src/shared/components/errors/SitesFallback500'
import { LDProviderWrapper } from './LDProviderWrapper'
import { isEcommRouteSegment } from 'src/public/js/ecommRouteConstants'

import TrackRestaurant from './TrackRestaurant'
import {
  I18nConfig,
  initLocalI18next,
  loadTranslations
} from 'src/translations/src'
import { OO_SELECTED_LANGUAGE } from 'src/public/components/locale_selector/common'
import { getCookie } from 'react-use-cookie'
import OOFallback500 from '../../errors/OOFallback500'

export type OORestaurant = RestaurantQuery['restaurantV2'] & {
  __typename: 'Restaurant'
  onlineOrderingEnabled: boolean
}
export type PromoBanners = PromoBannersQuery['promoBanners'] & {
  __typename: 'PromoBannerResponse'
}
export type DiningOptions = OoAvailabilityQuery['diningOptions']
export type RestaurantSiteContent = NonNullable<SiteData>['content'] & {
  __typename: 'SiteContent'
}
export type RestaurantConfig = SiteData['config']

export type DeletableRestaurantPage = RestaurantPage & { deleted?: boolean }

declare global {
  interface Window {
    __FLAGS_STATE__: LDFlagSet | undefined
  }
}

export type RestaurantContextType = {
  ooRestaurant?: OORestaurant
  refetchOORestaurant: () => Promise<ApolloQueryResult<RestaurantQuery>>
  ooPromoBanners?: PromoBanners
  diningOptions?: DiningOptions
  orderingDisabled?: boolean
  restaurant: SiteData
  sitePages?: RestaurantPage[] | null
  locations?: SiteRestaurantLocation[] | null
  selectedLocation: SiteRestaurantLocation
  updateLocation: (guid: string, skipReplace?: boolean) => void
  orderingUrl?: string | null
  reservationsUrl?: string | null
  phoneNumber?: string | null
  pixels?: string[] | null
  pixelsV2?: any | null
  host?: string
  toastProduct: ToastProduct
  hasIntlLocation: boolean
  currentPageType?: PageType
  i18n?: {
    currency?: string | null
    locale?: string | null
    country?: string | null
  }
  ecommSettings?: EcommSettings
  loadingEcommSettings: boolean
  isEcommMultiLocationEnabled: boolean
  locationExplicitlySelected: boolean
}

type Params = {
  locationGuid?: string
  slug?: string
}

type WithRouterProps = RequestContextProps & ProviderProps
type BaseProps = RouterSubstituteProps &
  ProviderProps & { allowLocationSwitching: boolean } & DebugProps

type ProviderProps = (EditorTrueProps | EditorFalseProps) & EditorSharedProps

type EditorSharedProps = {
  siteRestaurant?: SiteData | null
  siteRestaurantError?:
    | RestaurantDataByDomainQueryHookResult['error']
    | RestaurantDataByShortUrlQueryHookResult['error']
  siteRestaurantLoading?: boolean
  ooUsage?: RxDataUsage
  sitesUsage?: RxDataUsage
  shortUrl?: string
  currentPageType?: PageType
  ecommPath?: string
}

type EditorTrueProps = {
  isEditor: true
  routeInfo: RouterSubstituteProps
  site: SiteData
  sitePages?: RestaurantPage[] | null
}

type EditorFalseProps = {
  isEditor: false
  routeInfo?: null
  site?: null
  sitePages?: null
}

type RouterSubstituteProps = {
  path: string
  params: Params
  host: string
  history?: any
  staticContext?: any
}

type DebugProps = {
  overrides?: {
    menuPageConfigOverride?: MenuConfig
    orderPageConfigOverride?: OnlineOrderingConfig
    ooRestaurantOverride?: OORestaurantOverrideType
    metaOverride?: MetaOverrideType
    debugModeEnabled?: boolean
  }
}

export const LOCATION_KEY = 'toast-rx-location'

export const RestaurantContext = createContext<
  RestaurantContextType | undefined
>(undefined)

const RestaurantContextProvider = (
  props: React.PropsWithChildren<ProviderProps>
) =>
  props.isEditor ? (
    <RestaurantContextProviderWithoutRouter
      {...props}
      allowLocationSwitching={false}
      {...props.routeInfo}
      site={props.site}
    />
  ) : (
    <RestaurantContextProviderWithRouterAndDebug {...props} />
  )

const RestaurantContextProviderWithoutRouter = (
  props: React.PropsWithChildren<BaseProps>
) => {
  return (
    <RestaurantContextProviderBase
      {...props}
      {...props.routeInfo}
      siteRestaurant={props.site}
    />
  )
}

const RestaurantContextProviderWithRouterAndDebug = withRouter<
  any,
  React.ComponentType<WithRouterProps>
>((props: WithRouterProps) => {
  const host = getHost(props.staticContext)
  const history = useHistory()
  const params = useParams<Params>()

  const { staticContext, sitesUsage = RxDataUsage.Required } = props
  const {
    siteRestaurant,
    error: siteError,
    loading: siteLoading
  } = useRestaurantSiteData({
    staticContext,
    domainQueryHook: useRestaurantDataByDomainQuery,
    shortUrlQueryHook: useRestaurantDataByShortUrlQuery,
    mgmtGuidQueryHook: useRestaurantDataByMgmtGuidQuery,
    skipQuery: sitesUsage === RxDataUsage.None
  })

  const {
    data: defaultedSiteData,
    loading: defaultedSiteLoading,
    error: defaultedSiteError
  } = useDefaultSiteData({
    staticContext,
    siteLoading,
    siteRestaurant,
    siteError
  })

  const {
    ooRestaurantOverride,
    metaOverride,
    menuPageConfigOverride,
    orderPageConfigOverride,
    debugModeEnabled
  } = useDebug()
  const overrides = {
    menuPageConfigOverride,
    orderPageConfigOverride,
    ooRestaurantOverride,
    metaOverride,
    debugModeEnabled
  }

  if (
    isToastLocalRequest(staticContext) &&
    !defaultedSiteData &&
    !defaultedSiteLoading
  ) {
    return <NotFound404 />
  }

  return !defaultedSiteData && defaultedSiteLoading ? (
    <LoadingSpinnerOverlay color='#2B4FB9' />
  ) : (
    <RestaurantContextProviderBase
      {...props}
      host={host}
      history={history}
      params={params}
      path={props.match.path}
      allowLocationSwitching={true}
      siteRestaurant={defaultedSiteData}
      siteRestaurantError={defaultedSiteError}
      siteRestaurantLoading={defaultedSiteLoading}
      overrides={overrides}
    />
  )
})

const getLocationFromLocalStorage = (
  locations?: SiteRestaurantLocation[] | null
): SiteRestaurantLocation | undefined => {
  if (typeof window === 'undefined' || !locations) {
    return undefined
  }
  const storedLocation = localStorage.getItem(LOCATION_KEY)
  return storedLocation
    ? locations.find(
        (loc: SiteRestaurantLocation) => loc.externalId === storedLocation
      )
    : undefined
}

const RestaurantContextProviderBase = (
  props: React.PropsWithChildren<BaseProps>
) => {
  const {
    siteRestaurant,
    siteRestaurantError,
    siteRestaurantLoading,
    sitesUsage = RxDataUsage.Required,
    ooUsage = RxDataUsage.None,
    history,
    params,
    host,
    path,
    allowLocationSwitching,
    children,
    isEditor,
    sitePages,
    staticContext,
    currentPageType,
    ecommPath
  } = props
  // NOTE: DO NOT USE ANY react-router HOOKS in this base component.
  // NOTE: DO NOT USE ANY feature flags that rely on rx-level targeting in this component. When SSR'd the LD user is set to 'sites-web', no the rx guid
  const ooClient = useOOClient()
  const filteredSitePages = useMemo(() => {
    return sitePages?.filter((page: DeletableRestaurantPage) => !page.deleted)
  }, [sitePages])
  const { ooLocaleSelector } = useFlags()

  let currentLocation = siteRestaurant?.locations?.[0]
  let initialLocationExplicitlySelected = false
  const hasMultipleLocations = (siteRestaurant?.locations?.length || 0) > 1
  const paramLocation = params.locationGuid
  if (sitesUsage !== RxDataUsage.None) {
    if (paramLocation) {
      // If a user comes directly to a location link, we do want to 404 if that location doesn't exist
      currentLocation = siteRestaurant?.locations?.find(
        (loc: SiteRestaurantLocation) => loc.externalId === paramLocation
      )
      // Note: arriving via locationGuid URL is NOT considered an explicit selection.
      // The gate should still show for users who land via direct/shared links.
      // LocationCta handles dismissing the gate when users come from the location picker.
    } else if (params.slug && !isEcommRouteSegment(params.slug)) {
      // Only try to find location by slug if it's not a known ecommerce route segment
      // (e.g., "products", "categories", etc. should not be treated as location shortUrls)
      currentLocation = siteRestaurant?.locations?.find(
        (loc: SiteRestaurantLocation) =>
          loc.shortUrl === decodeURIComponent(params.slug || '')
      )
      // Note: arriving via shortUrl is NOT considered an explicit selection.
      // The gate should still show for users who land on a location via direct/Google links.
      // LocationCta handles dismissing the gate when users come from the location picker.
    } else {
      // Initialize from localStorage if no URL param is present
      const locationFromLocalStorage = getLocationFromLocalStorage(
        siteRestaurant?.locations
      )
      if (locationFromLocalStorage) {
        currentLocation = locationFromLocalStorage
        // Note: localStorage-based location is NOT considered an explicit selection.
        // The gate should still show so the user confirms their location.
      }
    }
  }
  // Single-location sites don't need explicit selection
  if (!hasMultipleLocations) {
    initialLocationExplicitlySelected = true
  }

  const [location, setLocation] = useState<
    SiteRestaurantLocation | undefined | null
  >(currentLocation)
  const [locationExplicitlySelected, setLocationExplicitlySelected] = useState(
    initialLocationExplicitlySelected
  )
  // Track if i18n translations are ready for editor mode
  // In editor mode, we need to wait for translations before rendering children that use Trans
  const [i18nReady, setI18nReady] = useState(!isEditor)

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      sitesUsage !== RxDataUsage.None &&
      !paramLocation &&
      !params.slug
    ) {
      const locationFromLocalStorage = getLocationFromLocalStorage(
        siteRestaurant?.locations
      )
      if (locationFromLocalStorage) {
        setLocation(locationFromLocalStorage)
        // Don't set locationExplicitlySelected here — localStorage presence
        // should not bypass the location selection gate.
      } else {
        // the value in local storage isnt a valid GUID for this Rx
        localStorage.removeItem(LOCATION_KEY)
      }
    } else if (location?.externalId) {
      localStorage.setItem(LOCATION_KEY, location.externalId)
    }
  }, [
    siteRestaurant,
    sitesUsage,
    paramLocation,
    params.slug,
    location,
    hasMultipleLocations
  ])

  // Determine if this is an ecommerce page early (before queries)
  const isEcomm = !!ecommPath || currentPageType === PageType.Ecommerce

  // A shortUrl is required to use OO and a lot of the associated Consumer App BFF queries.
  // It is required by the return type of this query.
  const skipOOQuery = useMemo(
    () =>
      ooUsage === RxDataUsage.None ||
      !location?.externalId ||
      !location?.shortUrl,
    [ooUsage, location]
  )
  const {
    data: restaurantData,
    error: ooError,
    loading: ooLoading,
    refetch
  } = useRestaurantQuery({
    variables: {
      restaurantGuid: location?.externalId || ''
    },
    skip: skipOOQuery,
    client: ooClient
  })

  const { data: availabilityData } = useOoAvailabilityQuery({
    variables: { restaurantGuid: location?.externalId || '' },
    skip: skipOOQuery,
    client: ooClient,
    fetchPolicy: typeof window === 'undefined' ? 'no-cache' : 'cache-first',
    onCompleted: (data) => {
      if (typeof window === 'undefined') {
        ooClient.cache.writeQuery({
          query: OoAvailabilityDocument,
          variables: { restaurantGuid: location?.externalId || '' },
          data
        })
      }
    }
  })

  const { data: promoData } = usePromoBannersQuery({
    variables: { restaurantGuid: location?.externalId || '' },
    skip:
      !location?.externalId || !!siteRestaurant?.config?.promoBannersDisabled,
    client: ooClient
  })

  // If updating the location on an OO page (i.e. the path includes a short URL),
  // this will only update the browser history stack if that location has a short URL.
  const updateLocation = useCallback(
    (guid: string) => {
      if (allowLocationSwitching) {
        const newLoc = siteRestaurant?.locations?.find(
          (loc: SiteRestaurantLocation) => loc.externalId === guid
        )
        const LOCATION_CHANGED_PARAM = 'locationChanged'
        if (newLoc) {
          localStorage.setItem(LOCATION_KEY, guid)
          setLocation(newLoc)
          setLocationExplicitlySelected(true)

          const pathParams = { ...params } as { slug?: string; rxGuid?: string }
          if (path.includes(':slug')) {
            if (newLoc.shortUrl) {
              pathParams.slug = newLoc.shortUrl
            } else {
              console.error('Cannot change path for restaurant location')
              return
            }
          }
          if (path.includes(':rxGuid')) {
            pathParams.rxGuid = newLoc.externalId
          }

          // Add location changed query parameter with location name
          const currentUrl = new URL(window.location.href)
          const queryParams = new URLSearchParams(currentUrl.search)
          queryParams.set(
            LOCATION_CHANGED_PARAM,
            encodeURIComponent(newLoc.name || newLoc.address1)
          )

          // Build appropriate path with params if needed
          let newPath = currentUrl.pathname
          if (!isEmpty(pathParams)) {
            newPath = generatePath(path, pathParams)
          }

          const newUrl = `${newPath}?${queryParams.toString()}`
          history.push(newUrl)
        }
      }
    },
    [siteRestaurant, history, path, allowLocationSwitching]
  )

  const siteRestaurantWithOverrides = useMemo(() => {
    return props.overrides?.debugModeEnabled && siteRestaurant
      ? {
          ...siteRestaurant,
          meta: { ...siteRestaurant.meta, ...props.overrides.metaOverride },
          config: {
            ...siteRestaurant.config,
            ooConfig: {
              ...siteRestaurant.config.ooConfig,
              ...props.overrides.orderPageConfigOverride
            },
            menuConfig: {
              ...siteRestaurant.config.menuConfig,
              ...props.overrides.menuPageConfigOverride
            }
          }
        }
      : siteRestaurant
  }, [
    props.overrides?.debugModeEnabled,
    props.overrides?.menuPageConfigOverride,
    props.overrides?.metaOverride,
    props.overrides?.orderPageConfigOverride,
    siteRestaurant
  ])

  const ooRestaurant = useMemo(() => {
    let restaurant = restaurantData?.restaurantV2 as OORestaurant | undefined
    return restaurant && props.overrides?.debugModeEnabled
      ? { ...restaurant, ...props.overrides.ooRestaurantOverride }
      : restaurant
  }, [
    props.overrides?.debugModeEnabled,
    props.overrides?.ooRestaurantOverride,
    restaurantData?.restaurantV2
  ])

  useEffect(() => {
    if (isEditor) {
      let userPreferredLocale
      if (ooLocaleSelector) {
        userPreferredLocale = getCookie(OO_SELECTED_LANGUAGE)
      }
      const i18nConfig: I18nConfig = {
        locale: ooRestaurant?.i18n?.locale ?? 'en-US',
        currency: ooRestaurant?.i18n?.currency ?? 'USD',
        country: ooRestaurant?.i18n?.country ?? 'US'
      }

      initLocalI18next(i18nConfig, userPreferredLocale)
        .then(() => loadTranslations())
        .then(() => {
          setI18nReady(true)
        })
        .catch((error) => {
          console.error('Failed to initialize i18next', error)
          setI18nReady(true) // unblock the spinner
        })
    }
  }, [ooRestaurant?.i18n, ooLocaleSelector, isEditor])

  const ooEnabled = useMemo(() => {
    if (availabilityData?.restaurantV2?.__typename !== 'Restaurant') {
      return false
    }

    // Toast-Local-only restaurants have onlineOrderingEnabled false but
    // toastLocalEnabled true. Accept the TL module on every TL surface, not just
    // /local, or ordering breaks for them on toast.app/r/:slug/order.
    return isToastLocalSurfaceRequest(staticContext)
      ? availabilityData?.restaurantV2?.onlineOrderingEnabled ||
          availabilityData?.restaurantV2?.toastLocalEnabled
      : availabilityData?.restaurantV2?.onlineOrderingEnabled
  }, [staticContext, availabilityData])

  const {
    data: ecommSettings,
    loading: loadingEcommSettings,
    error: ecommSettingsError
  } = useEcommSettingsQuery({
    variables: {
      input: {
        restaurantGuid: location?.externalId || '',
        siteGuid: siteRestaurantWithOverrides?.id || ''
      }
    },
    skip:
      !isEcomm ||
      ooUsage === RxDataUsage.None ||
      !siteRestaurantWithOverrides?.id ||
      !location?.externalId,
    client: ooClient
  })

  const isEcommMultiLocationEnabled = useMemo(
    () => isEcomm && !!ecommSettings?.getEcommSettings?.mlmEnabled,
    [isEcomm, isEditor, ecommSettings?.getEcommSettings?.mlmEnabled]
  )

  // WOO-4565: When ecomm multi-location is off and current location is not the default,
  // automatically switch to the default location
  useEffect(() => {
    // Only run on ecomm pages when MLM is disabled (treat undefined/null as disabled)
    if (
      !isEcomm ||
      loadingEcommSettings ||
      !ecommSettings?.getEcommSettings ||
      ecommSettings.getEcommSettings.mlmEnabled === true ||
      !location ||
      !siteRestaurant?.locations
    ) {
      return
    }

    const defaultLocationGuid =
      ecommSettings.getEcommSettings.defaultLocation?.guid

    // If there's a default location set and current location doesn't match it, switch
    if (defaultLocationGuid && location.externalId !== defaultLocationGuid) {
      // Find the default location in our site's locations
      const defaultLocation = siteRestaurant.locations.find(
        (loc) => loc.externalId === defaultLocationGuid
      )

      if (defaultLocation) {
        // Use existing updateLocation function - React handles the rest!
        updateLocation(defaultLocation.externalId)
      }
    }
  }, [
    isEcomm,
    loadingEcommSettings,
    ecommSettings,
    location,
    siteRestaurant?.locations,
    updateLocation
  ])

  if (
    (sitesUsage === RxDataUsage.Required && siteRestaurantError) ||
    (ooUsage === RxDataUsage.Required &&
      !ooLoading &&
      (ooError ||
        !restaurantData ||
        restaurantData?.restaurantV2?.__typename === 'GeneralError'))
  ) {
    if (sitesUsage === RxDataUsage.Required && siteRestaurantError) {
      reportError('Error querying Sites API', siteRestaurantError, {
        url: staticContext?.url,
        host: staticContext?.host
      })
    } else if (ooUsage === RxDataUsage.Required && ooError) {
      reportError('Error querying do-federated-gateway', ooError, {
        url: staticContext?.url,
        host: staticContext?.host
      })
      return (
        <OOFallback500
          meta={siteRestaurant?.meta}
          siteTheme={siteRestaurant?.theme}
          rxName={siteRestaurant?.name}
          rxLocation={location}
        />
      )
    } else if (ooUsage === RxDataUsage.Required && ecommSettingsError) {
      reportError(
        'Error querying do-federated-gateway for ecomm settings',
        ecommSettingsError,
        {
          url: staticContext?.url,
          host: staticContext?.host
        }
      )
    } else if (ooUsage === RxDataUsage.Required && restaurantData) {
      reportError('Error returned from do-federated-gateway', undefined, {
        restaurantData,
        url: staticContext?.url,
        host: staticContext?.host
      })
    } else if (ooUsage === RxDataUsage.Required && !location?.shortUrl) {
      return <NotFound404 errorMessage={t('restaurant-context.no-short-url')} />
    }

    return (
      <SitesFallback500
        staticContext={staticContext}
        siteTheme={siteRestaurant?.theme}
        rxLocations={siteRestaurant?.locations}
        meta={siteRestaurant?.meta}
        rxName={siteRestaurant?.name}
      />
    )
  }
  // In editor mode, wait for i18n translations to be loaded before rendering children
  // This prevents Trans components from crashing when rendered before translations are ready
  if (isEditor && !i18nReady) {
    return <LoadingSpinnerOverlay color='#2B4FB9' />
  }

  if (
    (!isEditor && !siteRestaurant && siteRestaurantLoading) ||
    (!restaurantData && ooLoading)
  ) {
    return <LoadingSpinnerOverlay color='#2B4FB9' />
  }

  if (!siteRestaurant || !location || !siteRestaurantWithOverrides) {
    let errorMessage: string | undefined = undefined
    if (!location) {
      errorMessage =
        'Please assign a location to this brand in order to use the page.'
    }
    return (
      <NotFound404
        errorMessage={errorMessage}
        meta={siteRestaurant?.meta}
        siteTheme={siteRestaurant?.theme}
      />
    )
  }

  const isOOBasic = siteRestaurant?.config?.hasFullCustomization === false
  let toastProduct: ToastProduct = ToastProduct.OOPro
  if (isToastAppOrderRequest(staticContext)) {
    toastProduct = ToastProduct.ToastApp
  } else if (isToastLocalRequest(staticContext)) {
    toastProduct = getToastLocalProduct(
      availabilityData?.restaurantV2 as OoAvailabilityQuery['restaurantV2']
    )
  } else if (isOOBasic) {
    toastProduct = ToastProduct.OOBasic
  } else if (isEcomm) {
    toastProduct = ToastProduct.OOEcommerce
  } else if (!siteRestaurant?.config?.isOnlineOrderingOnly) {
    toastProduct = ToastProduct.Sites
  }

  return (
    <LDProviderWrapper
      restaurantGuid={location?.externalId || ''}
      managementGroupGuid={siteRestaurant?.externalId || ''}
    >
      <RestaurantContext.Provider
        value={{
          ooRestaurant: ooRestaurant
            ? { ...ooRestaurant, onlineOrderingEnabled: ooEnabled || false }
            : undefined,
          refetchOORestaurant: refetch,
          ooPromoBanners: promoData?.promoBanners as PromoBanners,
          diningOptions: availabilityData?.diningOptions,
          orderingDisabled:
            !ooEnabled ||
            availabilityData?.diningOptions?.every(
              (option) =>
                !option.asapSchedule?.availableNow &&
                (!option.futureSchedule?.dates.length ||
                  !option.futureSchedule?.dates.filter(
                    (date) => date?.times?.length
                  ).length)
            ),
          restaurant: siteRestaurantWithOverrides,
          sitePages: filteredSitePages,
          locations: siteRestaurantWithOverrides.locations,
          selectedLocation: location,
          updateLocation,
          orderingUrl:
            location.onlineOrderingUrl ||
            siteRestaurantWithOverrides.onlineOrderingUrl,
          reservationsUrl:
            location.reservationsUrl ||
            siteRestaurantWithOverrides.reservationsUrl,
          phoneNumber: location.phoneNumber,
          pixels: siteRestaurantWithOverrides.pixels,
          pixelsV2: siteRestaurantWithOverrides.pixelsV2,
          host,
          toastProduct,
          hasIntlLocation: siteHasIntlLocation(siteRestaurant),
          currentPageType,
          i18n: ooRestaurant?.i18n || {
            currency: 'USD',
            locale: 'en-US',
            country: 'US'
          },
          ecommSettings: ecommSettings?.getEcommSettings,
          loadingEcommSettings,
          isEcommMultiLocationEnabled,
          locationExplicitlySelected
        }}
      >
        {children}
        {siteRestaurantLoading || ooLoading ? <LoadingSpinnerOverlay /> : null}
        <TrackRestaurant />
      </RestaurantContext.Provider>
    </LDProviderWrapper>
  )
}

export const useRestaurant = () => {
  const context = useContext(RestaurantContext)
  if (!context) {
    throw new Error(
      'useRestaurant must be used within a RestaurantContextProvider'
    )
  }
  return context
}

export const useOptionalRestaurant = () => {
  const context = useContext(RestaurantContext)
  return context
}

export default RestaurantContextProvider
